import axios from 'axios';
import {
  EvaluationContextType,
  EvaluationContextTypeValue,
  EvaluationErrorKey,
  EvaluationQuestionTypeValue,
  EvaluationRecipientStatusValue,
  EvaluationRequestStatusValue,
  EvaluationSourceValue,
} from '../util/evaluationContract';

export type RequestTag = { _id: string; name: string; archived: boolean };

export type EvaluationTagDto = { _id: string; name: string; archived: boolean };

export type SupplierSummary = {
  supplier: { _id: string; name: string; isDeleted: boolean };
  score: number | null;
  evaluationCount: number;
  evaluatedProjectsCount: number;
  lastEvaluatedAt: string | null;
  trend: { score: number; submittedAt: string }[];
  byTag: { tagId: string; name: string; archived: boolean; score: number; count: number }[];
  untagged: { score: number | null; count: number };
  comments: {
    responseId: string;
    requestId: string;
    questionText: string;
    score: number | null;
    skipped: boolean;
    comment: string;
    respondentName: string;
    projectName: string | null;
    submittedAt: string;
  }[];
  requests: {
    requestId: string;
    title: string;
    contextType: EvaluationContextTypeValue;
    projectName: string | null;
    projectNumber: string | null;
    sentDate: string;
    dueDate: string;
    status: EvaluationRequestStatusValue;
    sentCount: number;
    answeredCount: number;
    score: number | null;
    tags: RequestTag[];
  }[];
  canHide: boolean;
};

export type ResponseDetail = {
  responseId: string;
  requestId: string;
  title: string;
  supplierName: string;
  projectName: string | null;
  respondent: { name: string; email: string; title: string | null };
  submittedAt: string;
  late: boolean;
  score: number | null;
  hidden: { at: string; reason: string } | null;
  answers: {
    key: string;
    text: string;
    type: EvaluationQuestionTypeValue;
    score: number | null;
    skipped: boolean;
    comment: string | null;
    textAnswer: string | null;
  }[];
  canHide: boolean;
};

export type RequestStatus = {
  request: {
    _id: string;
    title: string;
    supplier: { _id: string; name: string; organizationNumber: string | null };
    context: {
      type: EvaluationContextTypeValue;
      projectId: string | null;
      projectName: string | null;
      projectNumber: string | null;
      areaName: string | null;
    };
    dueDate: string;
    status: EvaluationRequestStatusValue;
    createdAt: string;
    senderName: string | null;
    /** The person whose own row gets no email; null fields on a migrated utvärdering. */
    sender: { userId: string | null; email: string | null; name: string | null };
    source: EvaluationSourceValue;
    templateName: string | null;
    closedAt: string | null;
    cancelledAt: string | null;
    completedAt: string | null;
    tags: RequestTag[];
  };
  tiles: {
    recipients: number;
    answered: number;
    declined: number;
    waiting: number;
    failed: number;
    notSent: number;
    overdue: number;
  };
  recipients: {
    _id: string;
    name: string;
    email: string | null;
    title: string | null;
    status: EvaluationRecipientStatusValue;
    sentAt: string | null;
    openedAt: string | null;
    completedAt: string | null;
    declinedAt: string | null;
    responseId: string | null;
    late: boolean;
    score: number | null;
    userId: string | null;
    isSelf: boolean;
    canAnswerHere: boolean;
    hasLink: boolean;
  }[];
  canHide: boolean;
  canManage: boolean;
  canEditTags: boolean;
  canCancel: boolean;
  canReopen: boolean;
  viewerIsSender: boolean;
};

export type RecipientInput = { userId?: string; email?: string; name?: string };

export type SendOutcome = {
  sent: number;
  failed: { recipientId: string; email: string; reason: string }[];
  skipped: { email: string; reason: EvaluationErrorKey }[];
};

export type ManageResult = { status: RequestStatus; outcome: SendOutcome | null };

export type ReopenResult = ManageResult & {
  revoked: { duplicates: number; formerMembers: number };
};

export type BatchOutcome = {
  batchId: string;
  requestIds: string[];
  sent: number;
  failed: { supplierId: string | null; email: string | null; reason: string }[];
  skipped: { supplierId: string | null; email: string | null; reason: string }[];
  inProgress?: boolean;
};

export type ContextProject = {
  _id: string;
  name: string;
  number: string | null;
  isOpen: boolean;
  areas: { areaId: string; name: string; hasSupplier: boolean }[];
};

export type HistoricalSuggestion = {
  key: string;
  name: string | null;
  number: string | null;
  count: number;
};

export type StartEvaluationContext =
  | { type: typeof EvaluationContextType.Project; projectId: string }
  | {
      type: typeof EvaluationContextType.Historical;
      historical: { name: string; number: string };
    }
  | { type: typeof EvaluationContextType.None };

export type StartEvaluationBody = {
  supplierId: string;
  areaId: string | null;
  context: StartEvaluationContext;
  /** Ids from the org's erfa question list, in the order they are asked. */
  questionIds: string[];
  tagIds: string[];
  recipients: RecipientInput[];
  dueDayKey: string;
  title: string;
  idempotencyKey: string;
};

// The bare path: axiosHelper rewrites /api/... to /api/organizations/<slug>/....
const BASE_URL = '/api/evaluations';

class ServiceEvaluations {
  async getSupplierSummary(supplierId: string): Promise<SupplierSummary> {
    const response = await axios.get(`${BASE_URL}/suppliers/${supplierId}/summary`);
    return response.data;
  }

  async getResponse(responseId: string): Promise<ResponseDetail> {
    const response = await axios.get(`${BASE_URL}/responses/${responseId}`);
    return response.data;
  }

  async hideResponse(responseId: string, reason: string): Promise<void> {
    await axios.post(`${BASE_URL}/responses/${responseId}/hide`, { reason });
  }

  async getRequestStatus(requestId: string): Promise<RequestStatus> {
    const response = await axios.get(`${BASE_URL}/requests/${requestId}`);
    return response.data;
  }

  async closeRequest(requestId: string): Promise<ManageResult> {
    const response = await axios.post(`${BASE_URL}/requests/${requestId}/close`, {});
    return response.data;
  }

  async reopenRequest(requestId: string, dueDayKey?: string): Promise<ReopenResult> {
    const response = await axios.post(
      `${BASE_URL}/requests/${requestId}/reopen`,
      dueDayKey ? { dueDayKey } : {},
    );
    return response.data;
  }

  async changeDueDate(requestId: string, dueDayKey: string): Promise<ManageResult> {
    const response = await axios.post(`${BASE_URL}/requests/${requestId}/due-date`, { dueDayKey });
    return response.data;
  }

  async cancelRequest(requestId: string): Promise<ManageResult> {
    const response = await axios.post(`${BASE_URL}/requests/${requestId}/cancel`, {});
    return response.data;
  }

  async remindPending(requestId: string): Promise<ManageResult> {
    const response = await axios.post(`${BASE_URL}/requests/${requestId}/remind`, {});
    return response.data;
  }

  async addRecipients(requestId: string, recipients: RecipientInput[]): Promise<ManageResult> {
    const response = await axios.post(`${BASE_URL}/requests/${requestId}/recipients`, {
      recipients,
    });
    return response.data;
  }

  async resendRecipient(requestId: string, recipientId: string): Promise<ManageResult> {
    const response = await axios.post(
      `${BASE_URL}/requests/${requestId}/recipients/${recipientId}/resend`,
      {},
    );
    return response.data;
  }

  async changeRecipientEmail(
    requestId: string,
    recipientId: string,
    email: string,
  ): Promise<ManageResult> {
    const response = await axios.post(
      `${BASE_URL}/requests/${requestId}/recipients/${recipientId}/email`,
      { email },
    );
    return response.data;
  }

  async removeRecipient(requestId: string, recipientId: string): Promise<ManageResult> {
    const response = await axios.post(
      `${BASE_URL}/requests/${requestId}/recipients/${recipientId}/remove`,
      {},
    );
    return response.data;
  }

  async copyRecipientLink(requestId: string, recipientId: string): Promise<{ link: string }> {
    const response = await axios.post(
      `${BASE_URL}/requests/${requestId}/recipients/${recipientId}/link`,
      {},
    );
    return response.data;
  }

  async getAnswerPath(requestId: string, recipientId: string): Promise<{ path: string }> {
    const response = await axios.post(
      `${BASE_URL}/requests/${requestId}/recipients/${recipientId}/answer-link`,
      {},
    );
    return response.data;
  }

  async updateRequestTags(requestId: string, tagIds: string[]): Promise<ManageResult> {
    const response = await axios.post(`${BASE_URL}/requests/${requestId}/tags`, { tagIds });
    return response.data;
  }

  async getContexts(supplierId: string): Promise<ContextProject[]> {
    const response = await axios.get(`${BASE_URL}/contexts`, { params: { supplierId } });
    return response.data;
  }

  async getHistoricalSuggestions(q: string): Promise<HistoricalSuggestion[]> {
    const response = await axios.get(`${BASE_URL}/historical-contexts`, { params: { q } });
    return response.data;
  }

  async startEvaluation(body: StartEvaluationBody): Promise<BatchOutcome> {
    const response = await axios.post(BASE_URL, body);
    return response.data;
  }

  async getTags(): Promise<EvaluationTagDto[]> {
    const response = await axios.get(`${BASE_URL}/tags`);
    return response.data;
  }

  async addOrFindTag(name: string): Promise<EvaluationTagDto> {
    const response = await axios.post(`${BASE_URL}/tags`, { name });
    return response.data;
  }

  async renameTag(tagId: string, name: string): Promise<EvaluationTagDto> {
    const response = await axios.post(`${BASE_URL}/tags/${tagId}/rename`, { name });
    return response.data;
  }

  async archiveTag(tagId: string): Promise<EvaluationTagDto> {
    const response = await axios.post(`${BASE_URL}/tags/${tagId}/archive`, {});
    return response.data;
  }

  async unarchiveTag(tagId: string): Promise<EvaluationTagDto> {
    const response = await axios.post(`${BASE_URL}/tags/${tagId}/unarchive`, {});
    return response.data;
  }
}

export const serviceEvaluations = new ServiceEvaluations();
